import React, { useEffect } from 'react';
import styles from './UserDetails.module.scss';
import { useLocation } from 'react-router-dom';
import {
  ButtonProps,
  SearchProps,
  TabsKeys,
  UserDetailsTableProps,
} from 'enums/AccountsListEnums';
import COButton from 'shared/components/COButton';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import COTable from 'shared/components/COTable';
import { getColumns } from './UserDetailsColumns';
import AddUserModal from 'shared/components/AddUserModal';
import COSearch from 'shared/components/COSearch';
import AccountsService from 'services/AccountsService/accounts.service';
import { User } from 'models/User/User.model';

// Define a type for the record object
type RecordType = {
  id: string | number; // Adjust based on the actual type of `id`
  [key: string]: unknown; // Replace with specific properties if known
};

const UserDetails: React.FC = () => {
  const location = useLocation();
  const {
    loading,
    fetchAccounts,
    accountsList,
    createUserAccount,
    updateUserAccount,
    deleteUser,
    fetchRoles,
    roleList,
  } = AccountsService();
  const [showEditModalState, setShowEditModalState] =
    React.useState<boolean>(false);
  const [showDeleteModalState, setDeleteModalState] =
    React.useState<boolean>(false);
  const [addUserModal, setAddUserModal] = React.useState<boolean>(false);
  const [clickedRow, setClickRow] = React.useState<string | number | null>(
    null
  );
  const [clickedRowData, setClickedRowData] = React.useState<
    User | undefined
  >();
  const isActiveTab = (key: string) => {
    let currentPage = location.pathname.split('/').length - 1;
    return location.pathname.split('/')[currentPage] === key;
  };

  const handleRowOnClick = (record: RecordType) => setClickRow(record.id);

  const handleClickedRowData = (record: RecordType) => {
    const user: User = {
      ...record,
      id: typeof record.id === 'number' ? record.id : undefined,
    };
    setClickedRowData(user);
  };

  const handleOnRowEvents = (record: RecordType) => {
    handleRowOnClick(record);
    handleClickedRowData(record);
  };

  const handleDeleteModalStateConfirm = async (value: boolean) => {
    if (typeof clickedRow === 'number') deleteUser(clickedRow);

    setDeleteModalState(false);
  };

  const handleDeleteModalStateCancel = (value: boolean) =>
    setDeleteModalState(value);

  const handleEditModalState = (value: boolean) => setShowEditModalState(value);

  const handleCancelEditModal = () => setShowEditModalState(false);

  /** Handle open/close states for edit modal */
  const handleOpenEditModal = () => setShowEditModalState(true);

  /** Handle open/close states for delete modal */
  const handleOpenDeleteModal = () => setDeleteModalState(true);

  /** handle add user modal */
  const handleCancelAddUserModal = () => setAddUserModal(false);

  const handleConfirmAddUserModal = () => setAddUserModal(false);

  const handleOpenAddUserModal = () => setAddUserModal(true);

  const columns = getColumns({
    handleOpenDeleteModal,
    handleOpenEditModal,
  });

  useEffect(() => {
    fetchAccounts();
    fetchRoles();
  }, []);

  return (
    <div>
      <AddUserModal
        showModalState={addUserModal}
        handleModalCancel={handleCancelAddUserModal}
        handleModalConfirm={handleConfirmAddUserModal}
        createUserAccount={createUserAccount}
        updateUserAccount={updateUserAccount}
        roleList={roleList}
      />

      {isActiveTab(TabsKeys.USER_DETAILS) && (
        <div style={{ float: 'right', marginTop: '-4.5rem' }}>
          <COButton onClick={handleOpenAddUserModal}>
            <Add /> {ButtonProps.ADD_USER}
          </COButton>
        </div>
      )}

      <div className={styles.rowOne}>
        <div className={styles.searchContainer}>
          <COSearch
            placeholderString={SearchProps.SEARCH_PLACEHOLDER}
            border
            handleChange={e =>
              fetchAccounts({
                search: e.target.value,
              })
            }
          />
        </div>
      </div>

      <COTable
        loading={loading}
        columns={columns}
        showDeleteModalState={showDeleteModalState}
        showEditModalState={showEditModalState}
        data={accountsList}
        handleRowClick={handleOnRowEvents}
        handleDeleteModalStateCancel={handleDeleteModalStateCancel}
        handleDeleteModalStateConfirm={handleDeleteModalStateConfirm}
        handleEditModalStateConfirm={handleEditModalState}
        handleEditModalStateCancel={handleEditModalState}
        editModal={
          <AddUserModal
            preFetchedData={clickedRowData}
            showModalState={showEditModalState}
            handleModalCancel={handleCancelEditModal}
            handleModalConfirm={handleCancelEditModal}
            headingEdit={UserDetailsTableProps.EDIT_MODAL_HEADING}
            updateUserAccount={updateUserAccount}
            roleList={roleList}
          />
        }
        headingDelete={UserDetailsTableProps.DELETE_MODAL_HEADING}
        subheadingDelete={UserDetailsTableProps.DELTE_MODAL_SUBHEADING}
      />
    </div>
  );
};

export default UserDetails;
